import React from 'react';
import { Icon } from '../icons.jsx';
import {
  LizziLogo, Avatar, AvatarStack, StatusChip,
  Sidebar, UserMenu, MenuItem,
  Topbar, CommandPalette,
  ChatDock, ChatMessage, FormattedText, ChatBlock,
  ToastHost, confirmDialog, bidTitle,
  ProgressBar, StatPill,
  fmtMoney, fmtPct, fmtDate, fmtDateFull,
} from '../components.jsx';
import { NewBidModal, UploadModal, NotificationsPanel, notify } from '../modals.jsx';
import { MockData } from '../mock/index.js';
import { useData } from '../data/DataProvider.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { deleteBid, pollAnalysisStatus, retryAnalysis, updateBid, generateShortTitles } from '../data/repo.js';

const { useState, useEffect, useRef, useMemo, useCallback } = React;

// ---------- Menú de acciones por licitación (editar / eliminar) ----------
// El menú se renderiza en una capa fija (position: fixed) anclada al botón,
// para que pueda sobresalir de la tarjeta de la tabla (que recorta con overflow).
function BidActionsMenu({ bid, onOpen }) {
  const { demoMode, reload } = useData();
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({ top: r.bottom + 6, right: window.innerWidth - r.right });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    const close = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, place]);

  const remove = async (e) => {
    e.stopPropagation();
    setOpen(false);
    if (!(await confirmDialog({ title: 'Eliminar licitación', message: `¿Eliminar la licitación "${bid.title}"? Esta acción no se puede deshacer.`, confirmLabel: 'Eliminar', tone: 'danger', icon: 'Trash' }))) return;
    if (demoMode) { notify('Eliminada (modo demo · no se guarda).', 'CheckCircle'); return; }
    try {
      await deleteBid(bid.id);
      await reload();
      notify('Licitación eliminada.', 'CheckCircle');
    } catch (err) {
      notify('No se pudo eliminar: ' + (err?.message || 'error'), 'AlertTriangle');
    }
  };

  return (
    <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
      <button ref={btnRef} className="btn btn-ghost btn-icon" onClick={() => setOpen((o) => !o)}><Icon.MoreH size={14} /></button>
      {open && (
        <div ref={menuRef} className="scale-in" style={{
          position: 'fixed', top: coords.top, right: coords.right, zIndex: 200, minWidth: 180,
          background: 'var(--bg-1)', border: '1px solid var(--line-2)', borderRadius: 10,
          boxShadow: 'var(--shadow-3)', padding: 6,
        }}>
          <button type="button" className="menu-row" onClick={(e) => { e.stopPropagation(); setOpen(false); onOpen(); }}
            style={menuRowStyle}>
            <Icon.Edit size={14} /> Abrir y editar
          </button>
          {isAdmin && (
            <button type="button" className="menu-row" onClick={remove}
              style={{ ...menuRowStyle, color: 'var(--sem-danger)' }}>
              <Icon.Trash size={14} /> Eliminar
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const menuRowStyle = {
  display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left',
  background: 'transparent', border: 0, borderRadius: 7, padding: '8px 9px',
  cursor: 'pointer', fontSize: 13, color: 'var(--text-1)',
};


// ---------- Selector de estado inline (desplegable flotante) ----------
// Pulsa el chip de estado en la tabla/tablero y cambia el estado de la
// licitación sin abrir la ficha. Persiste con updateBid y refleja el cambio
// en window.MockData.BIDS de inmediato (mismo patrón que el chat).
function StatusSelect({ bid, compact = false, onChanged }) {
  const { demoMode, reload } = useData();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const FLOW = window.MockData.STATUS_FLOW || [];
  const LABELS = window.MockData.STATUS_LABELS || {};
  const current = LABELS[bid.status];

  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const top = Math.min(r.bottom + 6, window.innerHeight - 360);
    setCoords({ top: Math.max(8, top), left: r.left });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    const close = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, place]);

  const choose = async (e, status) => {
    e.stopPropagation();
    if (status === bid.status) { setOpen(false); return; }
    setOpen(false);
    if (demoMode) { notify('Estado actualizado (modo demo · no se guarda).', 'CheckCircle'); return; }
    setSaving(true);
    try {
      await updateBid(bid.id, { status });
      const arr = window.MockData.BIDS;
      const idx = arr.findIndex((b) => b.id === bid.id);
      if (idx >= 0) arr[idx] = { ...arr[idx], status };
      notify(`Estado → ${LABELS[status]?.label || status}`, 'CheckCircle');
      // Dentro de la ficha refrescamos solo esa licitación; en listados se
      // recarga el dataset completo (contadores, tablero…).
      if (onChanged) { onChanged(status); window.dispatchEvent(new CustomEvent('lizzi:bid-updated', { detail: bid.id })); }
      else reload();
    } catch (err) {
      notify('No se pudo cambiar el estado: ' + (err?.message || 'error'), 'AlertTriangle');
    } finally {
      setSaving(false);
    }
  };

  if (!current) return <StatusChip status={bid.status} />;

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }} onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Cambiar estado"
        className={`sem ${current.sem}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          border: '1px solid transparent', borderRadius: 999,
          opacity: saving ? 0.6 : 1, transition: 'border-color 120ms, background 120ms',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--line-3)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
      >
        <span className="chip-dot" style={{ background: 'currentColor' }}></span>
        {current.label}
        <Icon.ChevronDown size={11} style={{ opacity: 0.7 }} />
      </button>
      {open && (
        <div ref={menuRef} className="scale-in" style={{
          position: 'fixed', top: coords.top, left: coords.left, zIndex: 200, minWidth: 200,
          background: 'var(--bg-1)', border: '1px solid var(--line-2)', borderRadius: 12,
          boxShadow: 'var(--shadow-3)', padding: 6,
        }}>
          <div className="section-title" style={{ padding: '4px 9px 6px' }}>Cambiar estado</div>
          {FLOW.map((s) => {
            const m = LABELS[s];
            if (!m) return null;
            const isCur = s === bid.status;
            return (
              <button key={s} type="button" onClick={(e) => choose(e, s)} style={{
                ...menuRowStyle,
                background: isCur ? 'var(--bg-active)' : 'transparent',
              }}
                onMouseEnter={(e) => { if (!isCur) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={(e) => { if (!isCur) e.currentTarget.style.background = 'transparent'; }}>
                <span className={`sem ${m.sem}`} style={{ width: 8, height: 8, padding: 0, borderRadius: 99 }}></span>
                <span style={{ flex: 1 }}>{m.label}</span>
                {isCur && <Icon.Check size={13} style={{ color: 'var(--primary)' }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


// =============================================
// Bids list — filters, segmented view, table
// =============================================
function BidsScreen({ openBid }) {
  const { BIDS, STATUS_LABELS } = window.MockData;
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('table');
  const { reload, demoMode } = useData();
  const { isAdmin, roles = [] } = useAuth();
  const canEdit = isAdmin || roles.some((r) => ['redactor', 'revisor', 'firmante'].includes(r));
  const [, force] = useState(0);

  // Re-render cuando otra parte de la app (chat, ficha) modifica una licitación.
  useEffect(() => {
    const onUpd = () => force((n) => n + 1);
    window.addEventListener('lizzi:bid-updated', onUpd);
    return () => window.removeEventListener('lizzi:bid-updated', onUpd);
  }, []);

  // Títulos resumidos por IA: genera (una vez por sesión) los que falten.
  useEffect(() => {
    if (demoMode || !canEdit) return;
    if (!BIDS.some((b) => !b.shortTitle && b.title && b.title.length > 60)) return;
    try { if (sessionStorage.getItem('lizzi:short-titles-run')) return; sessionStorage.setItem('lizzi:short-titles-run', '1'); } catch (_) {}
    // La función procesa hasta 40 por llamada: se repite mientras queden.
    (async () => {
      try {
        for (let i = 0; i < 5; i++) {
          const t = await generateShortTitles();
          force((n) => n + 1);
          if (Object.keys(t).length < 40) break;
        }
      } catch (e) { console.warn('[lizzi] títulos resumidos:', e?.message || e); }
    })();
  }, [demoMode, canEdit]); // eslint-disable-line

  const FLOW = window.MockData.STATUS_FLOW || [];
  const tabs = [
    { id:'all', label:'Todas', count:BIDS.length },
    ...FLOW.map((s) => ({
      id: s,
      label: STATUS_LABELS[s]?.label || s,
      count: BIDS.filter((b) => b.status === s).length,
    })),
  ];

  const filter = (b) => {
    if (tab !== 'all' && b.status !== tab) return false;
    if (search && !((b.title||'')+(b.shortTitle||'')+(b.entity||'')+(b.code||'')+(b.company||'')+(b.region||'')).toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  };
  const list = BIDS.filter(filter);

  // Nº de lotes por licitación matriz (para mostrar "N lotes" en la matriz).
  const lotCount = {};
  BIDS.forEach((b) => { if (b.parentBidId) lotCount[b.parentBidId] = (lotCount[b.parentBidId] || 0) + 1; });


  // Si la licitación está en análisis, abrimos el overlay de progreso en
  // lugar de la ficha (ficha bloqueada hasta terminar) y sondeamos el estado
  // real de la BD. Si está en error, ofrecemos reintentar.
  const openGuarded = async (b) => {
    if (b.analysisStatus === 'error') {
      if (!(await confirmDialog({ title: 'Reintentar análisis', message: `El análisis de "${b.title}" falló${b.analysisError ? `:\n\n${b.analysisError}` : '.'}\n\n¿Reintentar ahora?`, confirmLabel: 'Reintentar', tone: 'primary', icon: 'Refresh' }))) return;
      window.dispatchEvent(new CustomEvent('lizzi:analysis-open', { detail: { bidId: b.id, title: b.title } }));
      window.dispatchEvent(new CustomEvent('lizzi:analysis-progress', { detail: { bidId: b.id, phase: 'analyzing', pct: 10 } }));
      try {
        const res = await retryAnalysis(b.id, b.analysisSourceUrl || null);
        if (res?.status === 'done') notify('Análisis completado.', 'CheckCircle');
        await reload();
      } catch (e) {
        notify('No se pudo reanalizar: ' + (e?.message || 'error'), 'AlertTriangle');
      }
      return;
    }
    if (b.analysisStatus === 'processing') {
      window.dispatchEvent(new CustomEvent('lizzi:analysis-open', { detail: { bidId: b.id, title: b.title } }));
      window.dispatchEvent(new CustomEvent('lizzi:analysis-progress', { detail: { bidId: b.id, phase: 'analyzing', pct: b.analysisPct || 20 } }));
      pollAnalysisStatus(b.id).then((res) => {
        if (res?.status === 'done' || res?.status === 'error') reload();
      });
      return;
    }
    openBid(b.id);
  };

  return (
    <div className="viewport-inner fade-in">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Pipeline · {BIDS.length} {BIDS.length === 1 ? 'licitación' : 'licitaciones'}</div>
          <h1 className="page-title">Licitaciones</h1>
          <p className="muted" style={{fontSize:14, marginTop:2}}>Todo el portfolio de propuestas del equipo. Filtra por estado, entidad o palabra clave.</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-outline" onClick={()=>window.dispatchEvent(new CustomEvent('lizzi:open-upload'))}><Icon.Upload size={14}/> Subir pliego</button>
          <button className="btn btn-primary" onClick={()=>window.dispatchEvent(new CustomEvent('lizzi:open-new-bid'))}><Icon.Plus size={14}/> Nueva licitación</button>
        </div>
      </div>

      <div style={{display:'flex',alignItems:'center',gap:10, marginBottom:12, flexWrap:'wrap'}}>
        <div className="tabs" style={{flex:'0 1 auto'}}>
          {tabs.map(t => (
            <div key={t.id} className={`tab ${tab===t.id?'active':''}`} onClick={()=>setTab(t.id)}>
              {t.label} <span style={{marginLeft:6, fontSize:11, color:'var(--text-4)'}}>{t.count}</span>
            </div>
          ))}
        </div>
        <div style={{marginLeft:'auto', display:'flex', alignItems:'center', gap:6}}>
          <div style={{display:'flex',alignItems:'center', gap:8, padding:'6px 10px', border:'1px solid var(--line-2)', borderRadius:8, background:'var(--bg-1)'}}>
            <Icon.Search size={13}/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar entidad, código, palabra…" style={{background:'transparent',border:0, fontSize:12.5, color:'var(--text-1)', width:220}}/>
          </div>
          <button className="btn btn-outline" style={{padding:'6px 10px'}} onClick={()=>notify('Filtros avanzados: estado, fechas, importes, región', 'Filter')}><Icon.Filter size={13}/></button>
          <div style={{display:'flex', border:'1px solid var(--line-2)', borderRadius:8, overflow:'hidden'}}>
            <button className="btn btn-ghost" style={{padding:'6px 8px', background: view==='table'?'var(--bg-2)':'transparent'}} onClick={()=>setView('table')} title="Tabla"><Icon.Hash size={13}/></button>
            <button className="btn btn-ghost" style={{padding:'6px 8px', background: view==='board'?'var(--bg-2)':'transparent'}} onClick={()=>setView('board')} title="Tablero"><Icon.Layout size={13}/></button>
          </div>
        </div>
      </div>

      {view === 'table' ? (
        <div className="card" style={{overflow:'hidden'}}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{width:60}}>EXPT.</th>
                <th style={{maxWidth:360}}>Licitación</th>
                <th style={{width:130}}>Estado</th>
                <th style={{width:110}}>Compañía</th>
                <th style={{width:130}}>Localización</th>
                <th style={{width:130}}>Base sin IVA</th>
                <th style={{width:100}}>Margen</th>
                <th style={{width:120}}>Equipo</th>
                <th style={{width:140}}>Plazo</th>
                <th style={{width:36}}></th>
              </tr>
            </thead>
            <tbody>
              {list.map(b => <BidRow key={b.id} bid={b} lots={lotCount[b.id] || 0} onOpen={()=>openGuarded(b)}/>)}
            </tbody>

          </table>
          {list.length === 0 && (
            <div style={{padding:60, textAlign:'center', color:'var(--text-3)'}}>
              <Icon.Briefcase size={28}/>
              <div style={{marginTop:10}}>No hay licitaciones que coincidan con el filtro.</div>
            </div>
          )}
        </div>
      ) : (
        <BidsBoard list={list} openBid={openGuarded} grouped={demoMode}/>
      )}
    </div>
  );
}

function BidRow({ bid, lots = 0, onOpen }) {
  const team = (bid.team || []).map(id => window.MockData.USERS.find(u => u.id === id)).filter(Boolean);
  const marginPositive = bid.margin > 8;
  return (
    <tr style={{cursor:'pointer'}} onClick={onOpen}>
      <td className="font-mono" style={{fontSize:11, color:'var(--text-3)'}}>{(bid.code || '').replace(/EXPT\s*/, '')}</td>
      <td style={{maxWidth:360}}>
        <div style={{display:'flex',alignItems:'center', gap:8, marginBottom:3}}>
          {bid.flagship && <Icon.Star size={11} style={{color:'var(--sem-warning)'}}/>}
          {bid.parentBidId && <span className="chip" style={{fontSize:10}}><Icon.Layers size={10}/> Lote {bid.lotNumber ?? ''}</span>}
          <span style={{fontSize:11, color:'var(--text-3)'}}>{bid.entityShort}</span>
        </div>
        <div style={{display:'flex', alignItems:'flex-start', gap:8}}>
          <div
            style={{
              fontSize:13.5, color:'var(--text-1)', lineHeight:1.35,
              display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical',
              overflow:'hidden', textOverflow:'ellipsis',
            }}
            title={bid.title}
          >
            {bidTitle(bid)}
          </div>
          {lots > 0 && <span className="chip" style={{fontSize:10, flexShrink:0}}><Icon.Layers size={10}/> {lots} {lots === 1 ? 'lote' : 'lotes'}</span>}
        </div>
      </td>
      <td>{bid.analysisStatus === 'processing'
        ? <span className="sem sem-violet" style={{fontSize:11}}><span className="spin" style={{display:'inline-flex'}}><Icon.Sparkles size={11}/></span> Analizando con Lizzi</span>
        : bid.analysisStatus === 'error'
        ? <span className="sem sem-danger" style={{fontSize:11}} title={bid.analysisError || 'El análisis falló'}><Icon.AlertTriangle size={11}/> Error</span>
        : <StatusSelect bid={bid}/>}</td>
      <td>{bid.company ? <span className="chip" style={{fontSize:11}}>{bid.company}</span> : <span style={{fontSize:11, color:'var(--text-4)'}}>—</span>}</td>
      <td style={{fontSize:12.5, color:'var(--text-2)', maxWidth:130}}>
        {bid.region
          ? <span style={{display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden'}} title={bid.region}>{bid.region}</span>
          : <span style={{color:'var(--text-4)'}}>—</span>}
      </td>
      <td className="font-mono" style={{fontSize:12.5}}>{fmtMoney(bid.budgetBase)}</td>
      <td>
        <span className={`sem ${marginPositive ? 'sem-success' : bid.margin>3 ? 'sem-warning' : 'sem-danger'}`}>
          {fmtPct(bid.margin)}
        </span>
      </td>
      <td>
        <AvatarStack users={team} size={22} max={3}/>
      </td>
      <td>
        {bid.deadline ? (
          <>
            <div style={{fontSize:12}}>{fmtDateFull(bid.deadline)}</div>
            <div style={{fontSize:11, color: bid.deadlineDays > 0 && bid.deadlineDays < 15 ? 'var(--sem-warning)' : 'var(--text-3)', marginTop:1}}>
              {bid.deadlineDays > 0 ? `en ${bid.deadlineDays} días` : `hace ${Math.abs(bid.deadlineDays)} días`}
            </div>
          </>
        ) : (
          <div style={{fontSize:12, color:'var(--text-4)'}}>Sin plazo</div>
        )}
      </td>
      <td><BidActionsMenu bid={bid} onOpen={onOpen} /></td>

    </tr>
  );
}

function BidsBoard({ list, openBid, grouped = false }) {
  const cols = window.MockData.STATUS_FLOW || ['prenotice','spotted','analysis','preparation','submitted','solvency','won','lost','discarded'];

  if (grouped) {
    const groups = [
      { title: 'Cribado', icon: 'Compass', cols: ['prenotice','spotted','discarded'] },
      { title: 'Ejecución', icon: 'Workflow', cols: ['analysis','preparation','submitted','solvency','won','lost'] },
    ];
    return (
      <div style={{display:'flex', flexDirection:'column', gap:18}}>
        {groups.map(g => {
          const GIco = Icon[g.icon];
          return (
            <div key={g.title}>
              <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:10, paddingLeft:2}}>
                <GIco size={14} style={{color:'var(--text-3)'}}/>
                <span className="section-title" style={{padding:0}}>{g.title}</span>
                <span style={{fontSize:11.5, color:'var(--text-4)'}}>{list.filter(b=>g.cols.includes(b.status)).length}</span>
              </div>
              <BoardColumns cols={g.cols} list={list} openBid={openBid}/>
            </div>
          );
        })}
      </div>
    );
  }

  return <BoardColumns cols={cols} list={list} openBid={openBid}/>;
}

function BoardColumns({ cols, list, openBid }) {
  return (
    <div style={{display:'grid', gridAutoFlow:'column', gridAutoColumns:'minmax(220px,1fr)', gap:12, overflow:'auto'}}>
      {cols.map(s => {
        const items = list.filter(b => b.status === s);
        const sm = window.MockData.STATUS_LABELS[s];
        if (!sm) return null;

        return (
          <div key={s} style={{background:'var(--bg-2)', borderRadius:10, padding:8, minWidth:0}}>
            <div style={{display:'flex',alignItems:'center', justifyContent:'space-between', padding:'6px 8px 10px'}}>
              <span className={`sem ${sm.sem}`} style={{fontSize:11}}><span className="chip-dot" style={{background:'currentColor'}}></span>{sm.label}</span>
              <span style={{fontSize:11.5, color:'var(--text-4)', fontFamily:'JetBrains Mono'}}>{items.length}</span>
            </div>
            <div style={{display:'flex', flexDirection:'column', gap:8}}>
              {items.map(b => (
                <div key={b.id} className="card" style={{padding:'12px', cursor:'pointer'}} onClick={()=>openBid(b)}>
                  {b.analysisStatus === 'processing' && <div style={{display:'flex',alignItems:'center',gap:6, fontSize:10.5, color:'var(--sem-violet)', marginBottom:6}}><span className="spin"><Icon.Sparkles size={11}/></span> Analizando con Lizzi</div>}
                  {b.analysisStatus === 'error' && <div style={{display:'flex',alignItems:'center',gap:6, fontSize:10.5, color:'var(--sem-danger)', marginBottom:6}}><Icon.AlertTriangle size={11}/> Error en análisis</div>}
                  <div className="font-mono" style={{fontSize:10, color:'var(--text-4)', marginBottom:6}}>{b.code}</div>
                  <div style={{fontSize:12.5, marginBottom:8}} title={b.title}>{bidTitle(b).slice(0,72)}{bidTitle(b).length>72?'…':''}</div>
                  {b.analysisStatus !== 'processing' && b.analysisStatus !== 'error' && (
                    <div style={{marginBottom:8}}><StatusSelect bid={b}/></div>
                  )}
                  <div style={{display:'flex',alignItems:'center', justifyContent:'space-between'}}>
                    <span style={{fontSize:11.5, color:'var(--text-3)', fontFamily:'JetBrains Mono'}}>{fmtMoney(b.budgetBase)}</span>
                    <AvatarStack users={(b.team||[]).map(id=> window.MockData.USERS.find(u=>u.id===id)).filter(Boolean)} size={20} max={3}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

window.BidsScreen = BidsScreen;


export { BidsScreen, BidRow, BidsBoard, StatusSelect };
